import { type ClassValue, clsx } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * Conditional classes, with later Tailwind utilities beating earlier ones.
 *
 * The merge half is what makes a component's `className` prop actually work: without it
 * `cn("px-3", "px-4")` renders both and the winner is whichever CSS rule the bundler emitted
 * last, which is not a thing a caller can reason about. shadcn/ui assumes this helper exists
 * and every component pasted in from it will import it.
 *
 * ── Why this is `extendTailwindMerge` and not plain `twMerge` ─────────────────
 *
 * `tailwind-merge` groups utilities by the CSS property they set, and it has to guess which
 * group a `text-*` class belongs to: `text-center` is alignment, `text-lg` is size,
 * `text-red-500` is colour. It knows Tailwind's own scale by name. It does not know ours.
 *
 * DESIGN.md §3.4 names the type scale `display`, `h1`, `body`, `caption` and so on, so
 * `text-body` is a **font size** — but to an unconfigured `twMerge` it is an unknown word and
 * it falls into the colour group. `cn("bg-brand text-on-brand", "text-body")` then merges two
 * "colours" and keeps the last: the button renders its label in the default text colour on a
 * brand-coloured background, at 2.3:1, and nothing anywhere errors.
 *
 * That is not hypothetical — it is what the primary button shipped as until axe caught it in
 * `e2e/accessibility.e2e.ts`. Teaching the merger the scale below is the fix, and the axe
 * assertion is what stops it coming back.
 */
const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      /*
       * Must stay in step with the `--text-*` tokens in `theme.css` **and** with any an app
       * adds on top of them — `figure` is `apps/booking`'s, declared in its own `styles.css`.
       * A size this list does not know about lands in the colour group and silently wins over
       * the real colour, which is the failure the note above describes.
       *
       * Both vocabularies are here: DESIGN.md's names, and the semantic ones aliased onto
       * them. `text-button` is the one that bites hardest — every shadcn button sets it, and
       * an unlisted `text-button` would eat the `text-ink` beside it.
       */
      "font-size": [
        {
          text: [
            "display-xl",
            "display-lg",
            "display-md",
            "headline",
            "card-title",
            "subhead",
            "body-lg",
            "body",
            "body-sm",
            "caption",
            "button",
            "eyebrow",
            "mono",
            "display",
            "figure",
            "h1",
            "h2",
            "h3",
            "event",
          ],
        },
      ],
    },
  },
});

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}
